<template>
  <div>
    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
      class="home3-drawer"
    >
      <div class="home3-drawer__header">
        <div class="home3-nav__brand">
          <v-img
            src="@/assets/images/logos/header_logo.png"
            max-height="40"
            max-width="40"
            class="home3-nav__brand-icon mr-2"
            contain
          />
          <span class="home3-nav__brand-text">
            <span class="home3-nav__brand-watch">Watch</span><span class="home3-nav__brand-on">On</span>
          </span>
        </div>
        <div class="home3-drawer__subtitle mt-2">School Bus Tracking</div>
      </div>

      <v-divider />

      <v-list dense class="home3-drawer__nav">
        <v-list-item
          v-for="(item, i) in navItems"
          :key="i"
          link
          @click="goTo(item.link)"
        >
          <v-list-item-icon>
            <v-icon color="mobilePrimary">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div class="home3-drawer__actions pa-4">
        <template v-if="!isUserLoggedIn">
          <v-btn block color="primary" class="mb-2" to="/login">{{ $t('Login') }}</v-btn>
          <v-btn block outlined color="mobilePrimary" to="/register">{{ $t('school_signup') }}</v-btn>
        </template>
        <v-btn
          v-else
          block
          color="primary"
          :to="dashboardRoute"
        >
          {{ $t('dashboard') }}
        </v-btn>
      </div>
    </v-navigation-drawer>

    <v-app-bar
      app
      :color="scrolled ? 'white' : 'mobilePrimary'"
      :flat="!scrolled"
      :dark="!scrolled"
      :elevation="scrolled ? 4 : 0"
      class="home3-nav"
      :class="{ 'home3-nav--scrolled': scrolled }"
    >
      <v-toolbar-title
        class="home3-nav__brand"
        :class="{ 'home3-nav__brand--scrolled': scrolled }"
        @click="$vuetify.goTo('#hero')"
      >
        <v-img
          src="@/assets/images/logos/header_logo.png"
          max-height="44"
          max-width="44"
          class="home3-nav__brand-icon mr-2"
          contain
        />
        <span class="home3-nav__brand-text">
          <span class="home3-nav__brand-watch">Watch</span><span class="home3-nav__brand-on">On</span>
        </span>
      </v-toolbar-title>

      <v-spacer />

      <div v-if="!isMobile" class="home3-nav__links d-flex align-center">
        <v-btn
          v-for="(item, i) in navItems"
          :key="i"
          text
          :class="scrolled ? 'home3-nav__link--dark' : 'home3-nav__link--light'"
          @click="$vuetify.goTo(item.link)"
        >
          {{ item.text }}
        </v-btn>

        <template v-if="!isUserLoggedIn">
          <v-btn
            text
            class="ml-2"
            :class="scrolled ? 'home3-nav__link--dark' : 'home3-nav__link--light'"
            to="/login"
          >
            {{ $t('Login') }}
          </v-btn>
          <v-btn
            rounded
            color="primary"
            class="ml-2 px-6"
            to="/register"
          >
            {{ $t('school_signup') }}
          </v-btn>
        </template>
        <v-btn
          v-else
          rounded
          color="primary"
          class="ml-2 px-6"
          :to="dashboardRoute"
        >
          {{ $t('dashboard') }}
        </v-btn>
      </div>

      <v-app-bar-nav-icon
        v-else
        :color="scrolled ? 'mobilePrimary' : 'white'"
        @click.stop="drawer = !drawer"
      />
    </v-app-bar>
  </div>
</template>

<script>
export default {
  props: {
    systemName: {
      type: String,
      required: true,
    },
    isUserLoggedIn: {
      type: Boolean,
      default: false,
    },
    userRole: {
      type: String,
      default: null,
    },
    scrolled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    drawer: false,
    isMobile: false,
    navItems: [
      { text: 'Home', link: '#hero', icon: 'mdi-home-outline' },
      { text: 'Features', link: '#features', icon: 'mdi-star-outline' },
      { text: 'About', link: '#about', icon: 'mdi-information-outline' },
      { text: 'How It Works', link: '#how-it-works', icon: 'mdi-cog-outline' },
    ],
  }),
  computed: {
    dashboardRoute() {
      return this.userRole === 'admin' ? '/admin-dashboard' : '/school-dashboard'
    },
  },
  mounted() {
    this.onResize()
    window.addEventListener('resize', this.onResize, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth < 960
      if (!this.isMobile) {
        this.drawer = false
      }
    },
    goTo(link) {
      this.drawer = false
      this.$vuetify.goTo(link)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './home3-theme.scss';

.home3-nav {
  transition: background 0.35s ease, box-shadow 0.35s ease;

  &__brand {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__brand-icon {
    flex-shrink: 0;
  }

  &__brand-text {
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1;
    white-space: nowrap;
  }

  &__brand-watch {
    color: #fff;
    transition: color 0.35s ease;
  }

  &__brand-on {
    color: #f5c518;
  }

  &__brand--scrolled {
    .home3-nav__brand-watch {
      color: $home3-navy;
    }
  }

  &__link--light {
    color: rgba(255, 255, 255, 0.95) !important;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;

    &:hover {
      color: #fff !important;
      background: rgba(255, 255, 255, 0.1) !important;
    }
  }

  &__link--dark {
    color: $home3-text-muted !important;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
  }
}

.home3-drawer {
  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 1.25rem 1rem;

    .home3-nav__brand-watch {
      color: $home3-navy;
    }
  }

  &__subtitle {
    font-size: 0.75rem;
    color: $home3-text-muted;
  }

  &__actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }
}
</style>
